/**
 * RemarkModal — TL+ adds or edits the remark on a task.
 * Writes tasks.remarks; the caller receives the new value via onSaved
 * for an optimistic list update (realtime confirms afterwards).
 */

import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../api/supabase.js';
import { htmlToText } from '../../utils/sanitizeHtml.js';
import Modal from '../ui/Modal.jsx';

const MAX_REMARK_LENGTH = 1000;

export default function RemarkModal({ open, task, onClose, onSaved }) {
  const [remark, setRemark] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const saveLockRef = useRef(false);

  useEffect(() => {
    if (open) {
      setRemark(task?.remarks || '');
      setError('');
      saveLockRef.current = false;
    }
  }, [open, task?.id, task?.remarks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saveLockRef.current || !task?.id) return;

    const trimmed = remark.trim();
    if (trimmed.length > MAX_REMARK_LENGTH) {
      setError(`Remark must be ${MAX_REMARK_LENGTH} characters or fewer.`);
      return;
    }

    saveLockRef.current = true;
    setSaving(true);
    setError('');

    try {
      const value = trimmed || null;
      const { data, error: updateError } = await supabase
        .from('tasks')
        .update({ remarks: value })
        .eq('id', task.id)
        .select('id');

      if (updateError || !data?.length) {
        if (updateError) console.error('[ORIN] Failed to save remark:', updateError.message);
        setError(updateError?.message || "You don't have permission to remark on this task.");
        return;
      }

      onSaved?.(task.id, value);
      onClose?.();
    } finally {
      saveLockRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      title="Task remark"
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn" disabled={saving}>
            Cancel
          </button>
          <button
            type="submit"
            form="remark-form"
            className="btn-primary"
            disabled={saving}
            aria-busy={saving}
          >
            {saving ? 'Saving…' : 'Save remark'}
          </button>
        </>
      }
    >
      <form id="remark-form" onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
        {task && (
          <p className="text-sm text-muted">
            On: <span className="font-medium text-ink">{htmlToText(task.title) || 'Untitled task'}</span>
          </p>
        )}
        <textarea
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          rows={4}
          maxLength={MAX_REMARK_LENGTH}
          placeholder="e.g. Needs client sign-off before design starts"
          disabled={saving}
          aria-label="Remark"
          className="w-full px-3 py-2 rounded-md border border-border-base bg-card text-sm text-ink placeholder:text-slate-400 focus:outline-none focus:ring-2"
        />
        <p className="text-xs text-muted -mt-1">
          Visible to everyone who can see this task. Leave empty and save to clear.
        </p>
        {error && (
          <div
            className="text-sm text-status-overdue bg-red-50 border border-red-200 rounded-md px-3 py-2"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
